/**
 * The Team Work composer's text field with an @ person picker. Typing `@` and a few letters offers people: the
 * team's people first, then the project's, then anyone in the organisation the directory finds (marked when they
 * are not on the project, and not pickable, since only members can be mentioned). Picking one writes their name
 * after the @, so `mentionsIn` (the workbench's own parser, teamworkMentions.ts) finds it when the message is sent.
 */

import type { MentionablePerson } from '@aioe/shared';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { routes } from '../../api/client.ts';
import { inputClass } from '../ui.tsx';

export interface PickablePerson extends MentionablePerson {
  /** Where they come from, for the list's second line. */
  where: 'team' | 'project' | 'organisation';
  /** False for someone in the organisation who is not on the project. */
  onProject: boolean;
}

const WHERE_WORDS: Record<PickablePerson['where'], string> = { team: 'On the team', project: 'On the project', organisation: 'In the organisation' };

/** The @ word being typed just before the caret, if any. */
function typingMention(text: string, caret: number): { start: number; query: string } | undefined {
  const before = text.slice(0, caret);
  const m = /(^|[^\p{L}\p{N}_])@([^\n@]{0,40})$/u.exec(before);
  if (!m) return undefined;
  const query = m[2]!;
  // A finished word followed by a space twice is not a mention any more.
  if (/\s\s$/.test(query)) return undefined;
  return { start: before.length - query.length - 1, query };
}

export function MentionComposer({
  value,
  onChange,
  people,
  projectId,
  onKnown,
  placeholder,
  label,
  onSubmit,
  isDisabled,
}: {
  value: string;
  onChange: (text: string) => void;
  /** The team's and the project's people, the team's first. */
  people: PickablePerson[];
  projectId: string;
  /** Someone picked from the organisation search: the composer remembers them so their mention is found on send. */
  onKnown: (p: PickablePerson) => void;
  placeholder: string;
  label: string;
  onSubmit: () => void;
  isDisabled?: boolean;
}) {
  const field = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const [found, setFound] = useState<PickablePerson[]>([]);
  const [rect, setRect] = useState<{ top: number; left: number; width: number; above: boolean }>();
  const [dismissed, setDismissed] = useState<number>();
  const typing = typingMention(value, caret);
  const open = Boolean(typing) && dismissed !== typing?.start;
  const q = (typing?.query ?? '').trim().toLowerCase();

  // The organisation's people, for two letters or more; the team's and the project's are matched here without a call.
  useEffect(() => {
    if (!open || q.length < 2) return;
    let live = true;
    const t = setTimeout(() => {
      routes
        .projectPeopleSearch(q, projectId, 8)
        .then((r) => live && setFound(r.people.map((p) => ({ subjectId: p.id, name: p.name, where: 'organisation' as const, onProject: p.onProject !== false }))))
        .catch(() => live && setFound([]));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [open, q, projectId]);

  const local = people.filter((p) => !q || p.name.toLowerCase().includes(q));
  const seen = new Set(local.map((p) => p.subjectId));
  const shown = [...local, ...(q.length >= 2 ? found.filter((p) => !seen.has(p.subjectId)) : [])].slice(0, 10);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = field.current?.getBoundingClientRect();
      if (!r) return;
      const above = window.innerHeight - r.bottom < 280;
      setRect({ top: above ? r.top - 4 : r.bottom + 4, left: r.left, width: Math.min(r.width, 360), above });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const pick = (p: PickablePerson) => {
    if (!typing || !p.onProject) return;
    const end = typing.start + 1 + typing.query.length;
    const next = `${value.slice(0, typing.start)}@${p.name} ${value.slice(end)}`;
    onChange(next);
    if (p.where === 'organisation') onKnown(p);
    const at = typing.start + p.name.length + 2;
    requestAnimationFrame(() => {
      field.current?.focus();
      field.current?.setSelectionRange(at, at);
      setCaret(at);
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={field}
        aria-label={label}
        role="combobox"
        aria-expanded={open && shown.length > 0}
        aria-autocomplete="list"
        className={`${inputClass} min-h-20 resize-y`}
        rows={3}
        value={value}
        placeholder={placeholder}
        disabled={isDisabled}
        onChange={(e) => {
          onChange(e.target.value);
          setCaret(e.target.selectionStart);
          setActive(0);
        }}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
        onKeyDown={(e) => {
          if (open && shown.length) {
            if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, shown.length - 1));
            else if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0));
            else if (e.key === 'Enter' || e.key === 'Tab') pick(shown[active] ?? shown[0]!);
            else if (e.key === 'Escape') setDismissed(typing?.start);
            else return;
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit();
          }
        }}
        onBlur={() => setTimeout(() => setDismissed(typing?.start), 150)}
        onFocus={() => setDismissed(undefined)}
      />
      {open &&
        rect &&
        shown.length > 0 &&
        createPortal(
          <ul
            role="listbox"
            aria-label="People to mention"
            style={{ position: 'fixed', left: rect.left, width: Math.max(rect.width, 256), ...(rect.above ? { bottom: window.innerHeight - rect.top } : { top: rect.top }) }}
            className="z-50 max-h-64 overflow-auto rounded-lg border border-border bg-background p-1 text-sm shadow-xl"
          >
            {shown.map((p, i) => (
              <li
                key={`${p.where}/${p.subjectId}`}
                role="option"
                aria-selected={i === active}
                aria-disabled={!p.onProject}
                className={`rounded-md px-2 py-1.5 ${p.onProject ? 'cursor-[var(--cursor-interactive)]' : 'text-muted'} ${i === active ? 'bg-surface-secondary' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(p)}
              >
                <span className="font-medium">{p.name}</span>
                <span className="block truncate text-xs text-muted">{p.onProject ? WHERE_WORDS[p.where] : 'Not on the project, so they cannot be mentioned'}</span>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
